import { useState, useEffect, useCallback } from 'react';
import {
  FileText, Plus, Edit2, Trash2, Loader2, Search, ChevronRight,
  BookOpen, Wrench, Image, Scale, Building2, Save, X, Eye,
  ToggleLeft, ToggleRight, GripVertical, Sparkles, Settings,
  ChevronDown, ChevronUp, AlertCircle, Copy, Star, Zap, PenLine, LayoutTemplate,
} from 'lucide-react';
import { api } from '@/utils/api';
import { ImageUploader } from '@/components/shared/ImageUploader';

// ==================== TYPES ====================

interface ProposalSection {
  id: string;
  name: string;
  slug: string;
  type: string;
  contentHtml: string;
  contentJson: any;
  sortOrder: number;
  isActive: boolean;
  scope: string;
  productCategoryId: string | null;
  productId: string | null;
  images: string[];
}

interface TechSpecSheet {
  id: string;
  name: string;
  productCategoryId: string | null;
  productId: string | null;
  specType: string;
  specs: any;
  images: string[];
  isActive: boolean;
}

interface ProposalTermOption {
  id: string;
  termName: string;
  termSlug: string;
  sortOrder: number;
  options: Array<{ key: string; label: string; isDefault?: boolean }>;
  isEditable: boolean;
  editableHint: string | null;
  conditionType: string | null;
  conditionConfig: any;
  allowCustom: boolean;
  isActive: boolean;
  scope: string;
}

interface ProposalTermTemplate {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  scope: string;
  isDefault: boolean;
  isActive: boolean;
  selections: Array<{ termId: string; termSlug: string; selectedOptionKey: string; customValue?: string }>;
}

interface InstallationReference {
  id: string;
  title: string;
  description: string | null;
  productCategoryId: string | null;
  locationType: string;
  location: string | null;
  images: string[];
  clientName: string | null;
  year: number | null;
  isActive: boolean;
}

interface CompanyLiterature {
  id: string;
  name: string;
  slug: string;
  type: string;
  contentHtml: string | null;
  contentJson: any;
  images: string[];
  scope: string;
  productCategoryId: string | null;
  isActive: boolean;
}

type Tab = 'sections' | 'tech-specs' | 'terms' | 'installation-refs' | 'literature';

const TABS: { key: Tab; label: string; icon: any; desc: string }[] = [
  { key: 'sections', label: 'Sections', icon: FileText, desc: 'Company intro, cover letters, recent projects' },
  { key: 'tech-specs', label: 'Tech Specs', icon: Wrench, desc: 'Product-specific technical specification sheets' },
  { key: 'terms', label: 'Terms & Conditions', icon: Scale, desc: 'Configurable proposal terms with option variants' },
  { key: 'installation-refs', label: 'Installation Refs', icon: Image, desc: 'Reference photos by product and location type' },
  { key: 'literature', label: 'Literature', icon: BookOpen, desc: 'Comparisons, scope of supply, clientele, FAQs' },
];

// ==================== MAIN PAGE ====================

export function ProposalBuilderPage() {
  const [tab, setTab] = useState<Tab>('sections');
  const [sections, setSections] = useState<ProposalSection[]>([]);
  const [techSpecs, setTechSpecs] = useState<TechSpecSheet[]>([]);
  const [terms, setTerms] = useState<ProposalTermOption[]>([]);
  const [termTemplates, setTermTemplates] = useState<ProposalTermTemplate[]>([]);
  const [installRefs, setInstallRefs] = useState<InstallationReference[]>([]);
  const [literature, setLiterature] = useState<CompanyLiterature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [secRes, specRes, termRes, tplRes, refRes, litRes] = await Promise.all([
        api.get('/proposal-builder/sections'),
        api.get('/proposal-builder/tech-specs'),
        api.get('/proposal-builder/terms'),
        api.get('/proposal-builder/term-templates'),
        api.get('/proposal-builder/installation-refs'),
        api.get('/proposal-builder/literature'),
      ]);
      setSections(secRes.data.data || []);
      setTechSpecs(specRes.data.data || []);
      setTerms(termRes.data.data || []);
      setTermTemplates(tplRes.data.data || []);
      setInstallRefs(refRes.data.data || []);
      setLiterature(litRes.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load proposal builder data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const stats = {
    sections: sections.filter(s => s.isActive).length,
    techSpecs: techSpecs.filter(s => s.isActive).length,
    terms: terms.filter(t => t.isActive).length,
    installRefs: installRefs.filter(r => r.isActive).length,
    literature: literature.filter(l => l.isActive).length,
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-3 text-2xl font-bold text-gray-900">
            <Settings size={28} className="text-[var(--ink-0)]" />
            Proposal Builder
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Configure reusable proposal content — sections, specs, terms, references, and literature.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-1.5">
            <span className="font-semibold text-blue-700">{stats.sections + stats.techSpecs + stats.terms + stats.installRefs + stats.literature}</span>
            <span className="text-blue-600"> total items</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-1 overflow-x-auto border-b border-gray-200 pb-px">
        {TABS.map(t => {
          const Icon = t.icon;
          const count = stats[t.key === 'tech-specs' ? 'techSpecs' : t.key === 'installation-refs' ? 'installRefs' : t.key as keyof typeof stats];
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 whitespace-nowrap rounded-t-lg border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                tab === t.key
                  ? 'border-[var(--ink-0)] text-[var(--ink-0)] bg-[var(--saffron-50)]/50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon size={16} />
              {t.label}
              <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                tab === t.key ? 'bg-[var(--saffron-50)] text-[var(--ink-0)]' : 'bg-gray-100 text-gray-500'
              }`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-[var(--saffron-600)]" />
        </div>
      ) : (
        <>
          {tab === 'sections' && <SectionsTab sections={sections} onRefresh={fetchAll} />}
          {tab === 'tech-specs' && <TechSpecsTab specs={techSpecs} onRefresh={fetchAll} />}
          {tab === 'terms' && <TermsTab terms={terms} templates={termTemplates} onRefresh={fetchAll} />}
          {tab === 'installation-refs' && <InstallRefsTab refs={installRefs} onRefresh={fetchAll} />}
          {tab === 'literature' && <LiteratureTab literature={literature} onRefresh={fetchAll} />}
        </>
      )}
    </div>
  );
}

// ==================== SECTIONS TAB ====================

function SectionsTab({ sections, onRefresh }: { sections: ProposalSection[]; onRefresh: () => void }) {
  const [editing, setEditing] = useState<ProposalSection | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setExpanded(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this section?')) return;
    try {
      await api.delete(`/proposal-builder/sections/${id}`);
      onRefresh();
    } catch {}
  };

  const scopeColors: Record<string, string> = {
    all: 'bg-gray-100 text-gray-600',
    commercial: 'bg-blue-100 text-blue-700',
    domestic: 'bg-green-100 text-green-700',
    product_specific: 'bg-purple-100 text-purple-700',
  };
  const typeColors: Record<string, string> = {
    static: 'bg-gray-100 text-gray-600',
    dynamic: 'bg-amber-100 text-amber-700',
    cover_letter: 'bg-indigo-100 text-indigo-700',
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Reusable proposal sections — company intro, cover letters, recent projects, why choose us, etc.</p>
        <button
          onClick={() => setEditing({ id: '', name: '', slug: '', type: 'static', contentHtml: '', contentJson: null, sortOrder: sections.length, isActive: true, scope: 'all', productCategoryId: null, productId: null, images: [] })}
          className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-3 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]"
        >
          <Plus size={16} /> Add Section
        </button>
      </div>

      {sections.map(s => (
        <div key={s.id} className={`rounded-xl border ${s.isActive ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'}`}>
          <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => toggle(s.id)}>
            <GripVertical size={16} className="text-gray-300" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">{s.name}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${typeColors[s.type] || ''}`}>{s.type}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${scopeColors[s.scope] || ''}`}>{s.scope}</span>
                <span className="text-[10px] text-gray-400">#{s.sortOrder}</span>
              </div>
              <p className="text-xs text-gray-400 font-mono">{s.slug}</p>
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={(e) => { e.stopPropagation(); setEditing(s); }} className="rounded p-1 text-gray-400 hover:text-[var(--ink-0)] hover:bg-[var(--saffron-50)]">
                <Edit2 size={14} />
              </button>
              <button onClick={(e) => { e.stopPropagation(); handleDelete(s.id); }} className="rounded p-1 text-gray-400 hover:text-red-600 hover:bg-red-50">
                <Trash2 size={14} />
              </button>
              {expanded.has(s.id) ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
            </div>
          </div>
          {expanded.has(s.id) && (
            <div className="border-t border-gray-100 px-4 py-3">
              <div className="prose prose-sm max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: s.contentHtml }} />
              {s.contentJson && (
                <div className="mt-3 rounded-lg bg-gray-50 p-3">
                  <p className="text-xs font-medium text-gray-500 mb-2">Structured Data:</p>
                  <pre className="text-xs text-gray-600 overflow-x-auto">{JSON.stringify(s.contentJson, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {editing && (
        <SectionEditModal
          section={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onRefresh(); }}
        />
      )}
    </div>
  );
}

function SectionEditModal({ section, onClose, onSaved }: { section: ProposalSection; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState(section);
  const [saving, setSaving] = useState(false);
  const isNew = !section.id;

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNew) {
        await api.post('/proposal-builder/sections', form);
      } else {
        await api.put(`/proposal-builder/sections/${form.id}`, form);
      }
      onSaved();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{isNew ? 'New Section' : 'Edit Section'}</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full rounded-lg border px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Slug</label>
              <input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} className="w-full rounded-lg border px-3 py-2 text-sm font-mono" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="w-full rounded-lg border px-3 py-2 text-sm">
                <option value="static">Static</option>
                <option value="dynamic">Dynamic</option>
                <option value="cover_letter">Cover Letter</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Scope</label>
              <select value={form.scope} onChange={e => setForm({ ...form, scope: e.target.value })} className="w-full rounded-lg border px-3 py-2 text-sm">
                <option value="all">All</option>
                <option value="commercial">Commercial</option>
                <option value="domestic">Domestic</option>
                <option value="product_specific">Product Specific</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Sort Order</label>
              <input type="number" value={form.sortOrder} onChange={e => setForm({ ...form, sortOrder: Number(e.target.value) })} className="w-full rounded-lg border px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Content (HTML)</label>
            <textarea value={form.contentHtml} onChange={e => setForm({ ...form, contentHtml: e.target.value })} rows={12} className="w-full rounded-lg border px-3 py-2 text-sm font-mono" />
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} className="rounded" />
              Active
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="rounded-lg border px-4 py-2 text-sm">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {isNew ? 'Create' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== TECH SPECS TAB ====================

const EMPTY_SPEC: TechSpecSheet = {
  id: '', name: '', productCategoryId: null, productId: null,
  specType: 'table', specs: { sections: [{ title: 'Specifications', rows: [{ label: '', value: '', unit: '' }] }] },
  images: [], isActive: true,
};

function TechSpecsTab({ specs, onRefresh }: { specs: TechSpecSheet[]; onRefresh: () => void }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<TechSpecSheet | null>(null);

  const toggle = (id: string) => {
    setExpanded(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this tech spec sheet?')) return;
    try { await api.delete(`/proposal-builder/tech-specs/${id}`); onRefresh(); } catch {}
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Product-specific technical specification sheets pulled into proposals based on product category.</p>
        <button
          onClick={() => setEditing({ ...EMPTY_SPEC, specs: JSON.parse(JSON.stringify(EMPTY_SPEC.specs)) })}
          className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-3 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]"
        >
          <Plus size={16} /> Add Tech Spec
        </button>
      </div>
      {specs.map(spec => (
        <div key={spec.id} className="rounded-xl border border-gray-200 bg-white">
          <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => toggle(spec.id)}>
            <Wrench size={16} className="text-gray-400" />
            <div className="flex-1">
              <span className="text-sm font-semibold text-gray-900">{spec.name}</span>
              <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-500">{spec.specType}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={(e) => { e.stopPropagation(); setEditing(spec); }} className="rounded p-1 text-gray-400 hover:text-[var(--ink-0)] hover:bg-[var(--saffron-50)]">
                <Edit2 size={14} />
              </button>
              <button onClick={(e) => { e.stopPropagation(); handleDelete(spec.id); }} className="rounded p-1 text-gray-400 hover:text-red-600 hover:bg-red-50">
                <Trash2 size={14} />
              </button>
              {expanded.has(spec.id) ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
            </div>
          </div>
          {expanded.has(spec.id) && spec.specs && (
            <div className="border-t border-gray-100 px-4 py-3 space-y-4">
              {(spec.specs.sections || []).map((section: any, idx: number) => (
                <div key={idx}>
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">{section.title}</h4>
                  <table className="w-full text-xs border">
                    <tbody>
                      {(section.rows || []).map((row: any, ri: number) => (
                        <tr key={ri} className={ri % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                          <td className="border px-3 py-1.5 font-medium text-gray-700 w-2/5">{row.label}</td>
                          <td className="border px-3 py-1.5 text-gray-600">{row.value}{row.unit ? ` ${row.unit}` : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      {specs.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <Wrench size={40} className="mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No tech spec sheets configured yet</p>
          <button onClick={() => setEditing({ ...EMPTY_SPEC, specs: JSON.parse(JSON.stringify(EMPTY_SPEC.specs)) })} className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]">
            <Plus size={16} /> Add First Spec Sheet
          </button>
        </div>
      )}

      {editing && (
        <TechSpecEditModal
          spec={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onRefresh(); }}
        />
      )}
    </div>
  );
}

function TechSpecEditModal({ spec, onClose, onSaved }: { spec: TechSpecSheet; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState(() => ({
    ...spec,
    specs: JSON.parse(JSON.stringify(spec.specs || { sections: [{ title: 'Specifications', rows: [{ label: '', value: '', unit: '' }] }] })),
  }));
  const [saving, setSaving] = useState(false);
  const isNew = !spec.id;

  const updateSection = (si: number, field: string, val: string) => {
    const s = { ...form.specs };
    s.sections[si] = { ...s.sections[si], [field]: val };
    setForm({ ...form, specs: s });
  };
  const updateRow = (si: number, ri: number, field: string, val: string) => {
    const s = JSON.parse(JSON.stringify(form.specs));
    s.sections[si].rows[ri][field] = val;
    setForm({ ...form, specs: s });
  };
  const addRow = (si: number) => {
    const s = JSON.parse(JSON.stringify(form.specs));
    s.sections[si].rows.push({ label: '', value: '', unit: '' });
    setForm({ ...form, specs: s });
  };
  const removeRow = (si: number, ri: number) => {
    const s = JSON.parse(JSON.stringify(form.specs));
    s.sections[si].rows.splice(ri, 1);
    setForm({ ...form, specs: s });
  };
  const addSection = () => {
    const s = JSON.parse(JSON.stringify(form.specs));
    s.sections.push({ title: 'New Section', rows: [{ label: '', value: '', unit: '' }] });
    setForm({ ...form, specs: s });
  };
  const removeSection = (si: number) => {
    const s = JSON.parse(JSON.stringify(form.specs));
    s.sections.splice(si, 1);
    setForm({ ...form, specs: s });
  };

  const handleSave = async () => {
    if (!form.name.trim()) { alert('Name is required'); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, specType: form.specType, specs: form.specs, isActive: form.isActive, productCategoryId: form.productCategoryId, productId: form.productId, images: form.images };
      if (isNew) {
        await api.post('/proposal-builder/tech-specs', payload);
      } else {
        await api.put(`/proposal-builder/tech-specs/${form.id}`, payload);
      }
      onSaved();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{isNew ? 'Add Tech Spec Sheet' : 'Edit Tech Spec Sheet'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Heat Pump — Technical Specifications" className="w-full rounded-lg border px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Spec Type</label>
              <select value={form.specType} onChange={e => setForm({ ...form, specType: e.target.value })} className="w-full rounded-lg border px-3 py-2 text-sm">
                <option value="table">Table</option>
                <option value="list">List</option>
                <option value="matrix">Matrix</option>
              </select>
            </div>
          </div>

          {/* Spec Sections Editor */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-700">Specification Sections</label>
              <button onClick={addSection} className="flex items-center gap-1 text-xs text-[var(--ink-0)] hover:text-[var(--ink-0)]">
                <Plus size={12} /> Add Section
              </button>
            </div>
            {(form.specs.sections || []).map((section: any, si: number) => (
              <div key={si} className="rounded-lg border border-gray-200 p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    value={section.title}
                    onChange={e => updateSection(si, 'title', e.target.value)}
                    placeholder="Section title"
                    className="flex-1 rounded border px-2 py-1.5 text-sm font-medium"
                  />
                  {form.specs.sections.length > 1 && (
                    <button onClick={() => removeSection(si)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                  )}
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500">
                      <th className="text-left px-2 py-1 w-2/5">Label</th>
                      <th className="text-left px-2 py-1">Value</th>
                      <th className="text-left px-2 py-1 w-20">Unit</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(section.rows || []).map((row: any, ri: number) => (
                      <tr key={ri}>
                        <td className="px-1 py-0.5"><input value={row.label} onChange={e => updateRow(si, ri, 'label', e.target.value)} className="w-full rounded border px-2 py-1 text-xs" placeholder="Parameter" /></td>
                        <td className="px-1 py-0.5"><input value={row.value} onChange={e => updateRow(si, ri, 'value', e.target.value)} className="w-full rounded border px-2 py-1 text-xs" placeholder="Value" /></td>
                        <td className="px-1 py-0.5"><input value={row.unit || ''} onChange={e => updateRow(si, ri, 'unit', e.target.value)} className="w-full rounded border px-2 py-1 text-xs" placeholder="Unit" /></td>
                        <td className="px-1 py-0.5">
                          {section.rows.length > 1 && (
                            <button onClick={() => removeRow(si, ri)} className="text-red-400 hover:text-red-600"><X size={12} /></button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button onClick={() => addRow(si)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-[var(--ink-0)] ml-1">
                  <Plus size={12} /> Add Row
                </button>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} className="rounded" />
              Active
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <button onClick={onClose} className="rounded-lg border px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {isNew ? 'Create' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== TERMS TAB ====================

const EMPTY_TERM: ProposalTermOption = {
  id: '', termName: '', termSlug: '', sortOrder: 0,
  options: [{ key: 'option_1', label: '', isDefault: true }],
  isEditable: false, editableHint: null, conditionType: null, conditionConfig: null, allowCustom: false,
  isActive: true, scope: 'all',
};

const EMPTY_TEMPLATE: ProposalTermTemplate = {
  id: '', name: '', slug: '', description: null, scope: 'all',
  isDefault: false, isActive: true, selections: [],
};

type TermSubTab = 'definitions' | 'templates';

function TermsTab({ terms, templates, onRefresh }: { terms: ProposalTermOption[]; templates: ProposalTermTemplate[]; onRefresh: () => void }) {
  const [subTab, setSubTab] = useState<TermSubTab>('definitions');
  const [editingTerm, setEditingTerm] = useState<ProposalTermOption | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<ProposalTermTemplate | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const handleDeleteTerm = async (id: string) => {
    if (!confirm('Delete this term?')) return;
    try { await api.delete(`/proposal-builder/terms/${id}`); onRefresh(); } catch {}
  };

  const handleDeleteTemplate = async (id: string) => {
    if (!confirm('Delete this template?')) return;
    try { await api.delete(`/proposal-builder/term-templates/${id}`); onRefresh(); } catch {}
  };

  const handleSetDefault = async (id: string) => {
    try { await api.post(`/proposal-builder/term-templates/${id}/set-default`); onRefresh(); } catch {}
  };

  const scopeColors: Record<string, string> = {
    all: 'bg-gray-100 text-gray-600',
    commercial: 'bg-blue-100 text-blue-700',
    domestic: 'bg-green-100 text-green-700',
  };

  // Helper: resolve a template's selection to display label
  const getSelectionLabel = (sel: { termSlug: string; selectedOptionKey: string; customValue?: string }) => {
    if (sel.customValue) return sel.customValue;
    const term = terms.find(t => t.termSlug === sel.termSlug);
    if (!term) return sel.selectedOptionKey;
    const opt = (term.options as any[]).find((o: any) => o.key === sel.selectedOptionKey);
    return opt?.label || sel.selectedOptionKey;
  };

  return (
    <div className="space-y-4">
      {/* Sub-tab navigation */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 border-b border-gray-200">
          <button
            onClick={() => setSubTab('definitions')}
            className={`flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              subTab === 'definitions' ? 'border-[var(--ink-0)] text-[var(--ink-0)]' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Scale size={15} /> Term Definitions
            <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${subTab === 'definitions' ? 'bg-[var(--saffron-50)] text-[var(--ink-0)]' : 'bg-gray-100 text-gray-500'}`}>
              {terms.length}
            </span>
          </button>
          <button
            onClick={() => setSubTab('templates')}
            className={`flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              subTab === 'templates' ? 'border-[var(--ink-0)] text-[var(--ink-0)]' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <LayoutTemplate size={15} /> Templates
            <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${subTab === 'templates' ? 'bg-[var(--saffron-50)] text-[var(--ink-0)]' : 'bg-gray-100 text-gray-500'}`}>
              {templates.length}
            </span>
          </button>
        </div>
        {subTab === 'definitions' && (
          <button
            onClick={() => setEditingTerm({ ...EMPTY_TERM, sortOrder: terms.length + 1, options: [{ key: 'option_1', label: '', isDefault: true }] })}
            className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-3 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]"
          >
            <Plus size={16} /> Add Term
          </button>
        )}
        {subTab === 'templates' && (
          <button
            onClick={() => setEditingTemplate({ ...EMPTY_TEMPLATE })}
            className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-3 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]"
          >
            <Plus size={16} /> Add Template
          </button>
        )}
      </div>

      {/* ===== TERM DEFINITIONS SUB-TAB ===== */}
      {subTab === 'definitions' && (
        <div className="space-y-3">
          <p className="text-sm text-gray-500">Individual terms are configurable building blocks. Each term has options and can support conditions and custom values.</p>
          {terms.map((term, idx) => {
            const isExpanded = expanded.has(term.id);
            return (
              <div key={term.id} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                <div
                  className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50/50 transition-colors"
                  onClick={() => toggle(term.id)}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {isExpanded ? <ChevronUp size={14} className="text-gray-400 shrink-0" /> : <ChevronDown size={14} className="text-gray-400 shrink-0" />}
                    <span className="text-xs font-bold text-gray-400 w-5 shrink-0">{idx + 1}.</span>
                    <h4 className="text-sm font-semibold text-gray-900 truncate">{term.termName}</h4>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-500 font-mono shrink-0">{term.termSlug}</span>
                    {term.isEditable && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] text-amber-700 shrink-0">Editable</span>}
                    {term.allowCustom && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] text-violet-700 shrink-0 flex items-center gap-0.5"><PenLine size={9} /> Custom</span>}
                    {term.conditionType && <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-[10px] text-yellow-700 shrink-0 flex items-center gap-0.5"><Zap size={9} /> {term.conditionType}</span>}
                    {term.scope !== 'all' && <span className={`rounded-full px-2 py-0.5 text-[10px] shrink-0 ${scopeColors[term.scope] || scopeColors.all}`}>{term.scope}</span>}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 ml-2" onClick={e => e.stopPropagation()}>
                    <button onClick={() => setEditingTerm(term)} className="rounded p-1 text-gray-400 hover:text-[var(--ink-0)] hover:bg-[var(--saffron-50)]">
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => handleDeleteTerm(term.id)} className="rounded p-1 text-gray-400 hover:text-red-500 hover:bg-red-50">
                      <Trash2 size={14} />
                    </button>
                    <span className={`ml-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${term.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {term.isActive ? 'Active' : 'Disabled'}
                    </span>
                  </div>
                </div>
                {isExpanded && (
                  <div className="border-t border-gray-100 px-4 py-3 bg-gray-50/30">
                    {term.editableHint && <p className="text-xs text-gray-400 mb-2 ml-7">{term.editableHint}</p>}
                    <div className="ml-7 space-y-1.5">
                      {(term.options as any[]).map((opt: any, oi: number) => (
                        <div key={oi} className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs ${opt.isDefault ? 'bg-[var(--saffron-50)] border border-[var(--ink-3)]' : 'bg-white border border-gray-100'}`}>
                          <div className={`w-3 h-3 rounded-full border-2 flex items-center justify-center shrink-0 ${opt.isDefault ? 'border-[var(--ink-0)]' : 'border-gray-300'}`}>
                            {opt.isDefault && <div className="w-1.5 h-1.5 rounded-full bg-[var(--ink-0)]" />}
                          </div>
                          <span className={opt.isDefault ? 'font-medium text-[var(--ink-0)]' : 'text-gray-600'}>{opt.label}</span>
                          {opt.isDefault && <span className="ml-auto text-[10px] font-medium text-[var(--saffron-600)]">DEFAULT</span>}
                        </div>
                      ))}
                    </div>
                    {term.conditionType && term.conditionConfig && (
                      <div className="mt-3 ml-7 rounded-lg bg-yellow-50 border border-yellow-200 px-3 py-2">
                        <p className="text-[10px] font-semibold text-yellow-700 uppercase tracking-wide mb-1">Condition: {term.conditionType}</p>
                        <div className="text-xs text-yellow-800 space-y-0.5">
                          {Object.entries(term.conditionConfig as Record<string, string>).map(([k, v]) => (
                            <div key={k}><span className="font-mono text-yellow-600">{k}:</span> {v}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {terms.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <Scale size={40} className="mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No term definitions yet</p>
              <button onClick={() => setEditingTerm({ ...EMPTY_TERM, options: [{ key: 'option_1', label: '', isDefault: true }] })} className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]">
                <Plus size={16} /> Add First Term
              </button>
            </div>
          )}
        </div>
      )}

      {/* ===== TEMPLATES SUB-TAB ===== */}
      {subTab === 'templates' && (
        <div className="space-y-3">
          <p className="text-sm text-gray-500">Templates group selected term values together. Sales picks a template when creating a proposal to pre-fill all T&C.</p>
          {templates.map(tpl => (
            <div key={tpl.id} className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="text-sm font-semibold text-gray-900">{tpl.name}</h4>
                  {tpl.isDefault && (
                    <span className="flex items-center gap-0.5 rounded-full bg-yellow-100 border border-yellow-300 px-2 py-0.5 text-[10px] font-semibold text-yellow-700">
                      <Star size={10} className="fill-yellow-500 text-yellow-500" /> DEFAULT
                    </span>
                  )}
                  <span className={`rounded-full px-2 py-0.5 text-[10px] ${scopeColors[tpl.scope] || scopeColors.all}`}>{tpl.scope}</span>
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-500 font-mono">{tpl.slug}</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {!tpl.isDefault && (
                    <button onClick={() => handleSetDefault(tpl.id)} className="rounded p-1 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50" title="Set as default">
                      <Star size={14} />
                    </button>
                  )}
                  <button onClick={() => setEditingTemplate(tpl)} className="rounded p-1 text-gray-400 hover:text-[var(--ink-0)] hover:bg-[var(--saffron-50)]">
                    <Edit2 size={14} />
                  </button>
                  <button onClick={() => handleDeleteTemplate(tpl.id)} className="rounded p-1 text-gray-400 hover:text-red-500 hover:bg-red-50">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              {tpl.description && <p className="text-xs text-gray-500 mb-3">{tpl.description}</p>}
              <div className="flex flex-wrap gap-1.5">
                {(tpl.selections || []).map((sel, si) => {
                  const term = terms.find(t => t.termSlug === sel.termSlug);
                  return (
                    <span key={si} className="inline-flex items-center gap-1 rounded-lg bg-gray-50 border border-gray-200 px-2.5 py-1.5 text-xs">
                      <span className="font-medium text-gray-700">{term?.termName || sel.termSlug}:</span>
                      <span className="text-gray-500 max-w-[200px] truncate">{getSelectionLabel(sel)}</span>
                    </span>
                  );
                })}
                {(!tpl.selections || tpl.selections.length === 0) && (
                  <span className="text-xs text-gray-400 italic">No term selections configured</span>
                )}
              </div>
            </div>
          ))}
          {templates.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <LayoutTemplate size={40} className="mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No templates created yet</p>
              <p className="text-xs text-gray-400 mt-1">Templates let sales pre-fill all terms with one click</p>
              <button onClick={() => setEditingTemplate({ ...EMPTY_TEMPLATE })} className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]">
                <Plus size={16} /> Create First Template
              </button>
            </div>
          )}
        </div>
      )}

      {editingTerm && (
        <TermEditModal term={editingTerm} onClose={() => setEditingTerm(null)} onSaved={() => { setEditingTerm(null); onRefresh(); }} />
      )}
      {editingTemplate && (
        <TemplateEditModal template={editingTemplate} terms={terms} onClose={() => setEditingTemplate(null)} onSaved={() => { setEditingTemplate(null); onRefresh(); }} />
      )}
    </div>
  );
}

// ==================== TERM EDIT MODAL ====================

function TermEditModal({ term, onClose, onSaved }: { term: ProposalTermOption; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState(() => ({
    ...term,
    options: JSON.parse(JSON.stringify(term.options || [])),
    conditionType: term.conditionType || '',
    conditionConfig: term.conditionConfig ? JSON.parse(JSON.stringify(term.conditionConfig)) : {},
    allowCustom: term.allowCustom || false,
  }));
  const [saving, setSaving] = useState(false);
  const isNew = !term.id;

  const updateOption = (idx: number, field: string, val: any) => {
    const opts = [...form.options];
    opts[idx] = { ...opts[idx], [field]: val };
    setForm({ ...form, options: opts });
  };
  const setDefault = (idx: number) => {
    const opts = form.options.map((o: any, i: number) => ({ ...o, isDefault: i === idx }));
    setForm({ ...form, options: opts });
  };
  const addOption = () => {
    const opts = [...form.options, { key: `option_${form.options.length + 1}`, label: '', isDefault: false }];
    setForm({ ...form, options: opts });
  };
  const removeOption = (idx: number) => {
    if (form.options.length <= 1) return;
    const opts = form.options.filter((_: any, i: number) => i !== idx);
    if (!opts.some((o: any) => o.isDefault)) opts[0].isDefault = true;
    setForm({ ...form, options: opts });
  };

  const updateConditionField = (key: string, val: string) => {
    setForm({ ...form, conditionConfig: { ...form.conditionConfig, [key]: val } });
  };

  const handleSave = async () => {
    if (!form.termName.trim()) { alert('Term name is required'); return; }
    if (!form.termSlug.trim()) { alert('Term slug is required'); return; }
    if (form.options.some((o: any) => !o.label.trim())) { alert('All options must have a label'); return; }
    setSaving(true);
    try {
      const payload = {
        termName: form.termName, termSlug: form.termSlug, sortOrder: form.sortOrder,
        options: form.options, isEditable: form.isEditable, editableHint: form.editableHint || null,
        conditionType: form.conditionType || null,
        conditionConfig: form.conditionType ? form.conditionConfig : null,
        allowCustom: form.allowCustom,
        isActive: form.isActive, scope: form.scope,
      };
      if (isNew) {
        await api.post('/proposal-builder/terms', payload);
      } else {
        await api.put(`/proposal-builder/terms/${form.id}`, payload);
      }
      onSaved();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{isNew ? 'Add Term' : 'Edit Term'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          {/* Name + Slug */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Term Name *</label>
              <input value={form.termName} onChange={e => setForm({ ...form, termName: e.target.value })} placeholder="e.g. Payment Terms" className="w-full rounded-lg border px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Slug *</label>
              <input value={form.termSlug} onChange={e => setForm({ ...form, termSlug: e.target.value })} placeholder="e.g. payment_terms" className="w-full rounded-lg border px-3 py-2 text-sm font-mono" />
            </div>
          </div>
          {/* Scope, Sort, Editable */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Scope</label>
              <select value={form.scope} onChange={e => setForm({ ...form, scope: e.target.value })} className="w-full rounded-lg border px-3 py-2 text-sm">
                <option value="all">All</option>
                <option value="commercial">Commercial</option>
                <option value="domestic">Domestic</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Sort Order</label>
              <input type="number" value={form.sortOrder} onChange={e => setForm({ ...form, sortOrder: Number(e.target.value) })} className="w-full rounded-lg border px-3 py-2 text-sm" />
            </div>
            <div className="flex flex-col justify-end gap-1">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.isEditable} onChange={e => setForm({ ...form, isEditable: e.target.checked })} className="rounded" />
                Editable by sales
              </label>
            </div>
          </div>
          {form.isEditable && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Editable Hint</label>
              <input value={form.editableHint || ''} onChange={e => setForm({ ...form, editableHint: e.target.value || null })} placeholder="e.g. Sales can adjust the percentage split" className="w-full rounded-lg border px-3 py-2 text-sm" />
            </div>
          )}

          {/* Allow Custom + Condition Type */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2 pt-4">
              <input type="checkbox" checked={form.allowCustom} onChange={e => setForm({ ...form, allowCustom: e.target.checked })} id="allowCustom" className="rounded" />
              <label htmlFor="allowCustom" className="text-sm text-gray-700 flex items-center gap-1">
                <PenLine size={13} className="text-violet-500" /> Allow custom value
              </label>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Condition Type</label>
              <select value={form.conditionType} onChange={e => setForm({ ...form, conditionType: e.target.value })} className="w-full rounded-lg border px-3 py-2 text-sm">
                <option value="">None</option>
                <option value="stock_based">Stock-Based</option>
                <option value="value_based">Value-Based</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>

          {/* Condition Config */}
          {form.conditionType === 'stock_based' && (
            <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-3 space-y-2">
              <p className="text-xs font-semibold text-yellow-700">Stock-Based Condition</p>
              <div>
                <label className="block text-[10px] font-medium text-yellow-800 mb-0.5">If in stock</label>
                <input value={form.conditionConfig?.ifInStock || ''} onChange={e => updateConditionField('ifInStock', e.target.value)} placeholder="e.g. Ready stock: Immediate delivery" className="w-full rounded border border-yellow-300 px-2.5 py-1.5 text-xs bg-white" />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-yellow-800 mb-0.5">If not in stock</label>
                <input value={form.conditionConfig?.ifNotInStock || ''} onChange={e => updateConditionField('ifNotInStock', e.target.value)} placeholder="e.g. 10-15 working days from order confirmation" className="w-full rounded border border-yellow-300 px-2.5 py-1.5 text-xs bg-white" />
              </div>
            </div>
          )}
          {form.conditionType === 'value_based' && (
            <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-3 space-y-2">
              <p className="text-xs font-semibold text-yellow-700">Value-Based Condition</p>
              <div>
                <label className="block text-[10px] font-medium text-yellow-800 mb-0.5">Threshold (amount)</label>
                <input value={form.conditionConfig?.threshold || ''} onChange={e => updateConditionField('threshold', e.target.value)} placeholder="e.g. 100000" className="w-full rounded border border-yellow-300 px-2.5 py-1.5 text-xs bg-white" />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-yellow-800 mb-0.5">If above threshold</label>
                <input value={form.conditionConfig?.ifAbove || ''} onChange={e => updateConditionField('ifAbove', e.target.value)} placeholder="Option for high-value orders" className="w-full rounded border border-yellow-300 px-2.5 py-1.5 text-xs bg-white" />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-yellow-800 mb-0.5">If below threshold</label>
                <input value={form.conditionConfig?.ifBelow || ''} onChange={e => updateConditionField('ifBelow', e.target.value)} placeholder="Option for standard orders" className="w-full rounded border border-yellow-300 px-2.5 py-1.5 text-xs bg-white" />
              </div>
            </div>
          )}
          {form.conditionType === 'custom' && (
            <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-3 space-y-2">
              <p className="text-xs font-semibold text-yellow-700">Custom Condition (key-value pairs)</p>
              <div>
                <label className="block text-[10px] font-medium text-yellow-800 mb-0.5">Condition Rule</label>
                <input value={form.conditionConfig?.rule || ''} onChange={e => updateConditionField('rule', e.target.value)} placeholder="Describe the condition logic" className="w-full rounded border border-yellow-300 px-2.5 py-1.5 text-xs bg-white" />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-yellow-800 mb-0.5">Default Value</label>
                <input value={form.conditionConfig?.defaultValue || ''} onChange={e => updateConditionField('defaultValue', e.target.value)} placeholder="Fallback value" className="w-full rounded border border-yellow-300 px-2.5 py-1.5 text-xs bg-white" />
              </div>
            </div>
          )}

          {/* Options Editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-700">Options (click radio to set default)</label>
              <button onClick={addOption} className="flex items-center gap-1 text-xs text-[var(--ink-0)] hover:text-[var(--ink-0)]">
                <Plus size={12} /> Add Option
              </button>
            </div>
            <div className="space-y-2">
              {form.options.map((opt: any, oi: number) => (
                <div key={oi} className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${opt.isDefault ? 'border-[var(--ink-3)] bg-[var(--saffron-50)]/50' : 'border-gray-200'}`}>
                  <input type="radio" checked={opt.isDefault} onChange={() => setDefault(oi)} className="text-[var(--ink-0)] cursor-pointer" />
                  <input value={opt.key} onChange={e => updateOption(oi, 'key', e.target.value)} placeholder="key" className="w-28 rounded border px-2 py-1 text-xs font-mono bg-gray-50" />
                  <input value={opt.label} onChange={e => updateOption(oi, 'label', e.target.value)} placeholder="Option text..." className="flex-1 rounded border px-2 py-1 text-xs" />
                  {form.options.length > 1 && (
                    <button onClick={() => removeOption(oi)} className="text-red-400 hover:text-red-600"><X size={14} /></button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} className="rounded" />
              Active
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <button onClick={onClose} className="rounded-lg border px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {isNew ? 'Create' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== TEMPLATE EDIT MODAL ====================

function TemplateEditModal({ template, terms, onClose, onSaved }: {
  template: ProposalTermTemplate; terms: ProposalTermOption[]; onClose: () => void; onSaved: () => void;
}) {
  const [form, setForm] = useState(() => {
    const selections = JSON.parse(JSON.stringify(template.selections || []));
    return { ...template, selections };
  });
  const [saving, setSaving] = useState(false);
  const isNew = !template.id;

  // Build a map of existing selections by termSlug for quick lookup
  const selMap = new Map<string, { selectedOptionKey: string; customValue?: string }>();
  (form.selections || []).forEach((s: any) => selMap.set(s.termSlug, s));

  const activeTerms = terms.filter(t => t.isActive);

  const getSelection = (termSlug: string) => selMap.get(termSlug);

  const updateSelection = (term: ProposalTermOption, optionKey: string, customValue?: string) => {
    const newSelections = [...form.selections];
    const idx = newSelections.findIndex((s: any) => s.termSlug === term.termSlug);
    const entry = { termId: term.id, termSlug: term.termSlug, selectedOptionKey: optionKey, ...(customValue ? { customValue } : {}) };
    if (idx >= 0) {
      newSelections[idx] = entry;
    } else {
      newSelections.push(entry);
    }
    setForm({ ...form, selections: newSelections });
  };

  const removeSelection = (termSlug: string) => {
    setForm({ ...form, selections: form.selections.filter((s: any) => s.termSlug !== termSlug) });
  };

  const handleSave = async () => {
    if (!form.name.trim()) { alert('Template name is required'); return; }
    if (!form.slug.trim()) { alert('Slug is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, slug: form.slug, description: form.description || null,
        scope: form.scope, isDefault: form.isDefault, isActive: form.isActive,
        selections: form.selections,
      };
      if (isNew) {
        await api.post('/proposal-builder/term-templates', payload);
      } else {
        await api.put(`/proposal-builder/term-templates/${form.id}`, payload);
      }
      onSaved();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{isNew ? 'Create Template' : 'Edit Template'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          {/* Name, Slug, Scope */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Template Name *</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Standard Commercial" className="w-full rounded-lg border px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Slug *</label>
              <input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="e.g. standard_commercial" className="w-full rounded-lg border px-3 py-2 text-sm font-mono" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Scope</label>
              <select value={form.scope} onChange={e => setForm({ ...form, scope: e.target.value })} className="w-full rounded-lg border px-3 py-2 text-sm">
                <option value="all">All</option>
                <option value="commercial">Commercial</option>
                <option value="domestic">Domestic</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <input value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value || null })} placeholder="Brief description of when to use this template..." className="w-full rounded-lg border px-3 py-2 text-sm" />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.isDefault} onChange={e => setForm({ ...form, isDefault: e.target.checked })} className="rounded" />
              <Star size={13} className="text-yellow-500" /> Set as default template
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} className="rounded" />
              Active
            </label>
          </div>

          {/* Term Selections */}
          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">Term Selections</h4>
            {activeTerms.length === 0 ? (
              <p className="text-xs text-gray-400 italic">No active terms defined. Create term definitions first.</p>
            ) : (
              <div className="space-y-3">
                {activeTerms.map(term => {
                  const sel = getSelection(term.termSlug);
                  const isCustomSelected = sel?.selectedOptionKey === '__custom__';
                  return (
                    <div key={term.id} className="rounded-lg border border-gray-200 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-800">{term.termName}</span>
                          {term.conditionType && (
                            <span className="flex items-center gap-0.5 rounded-full bg-yellow-100 px-1.5 py-0.5 text-[10px] text-yellow-700">
                              <Zap size={9} /> {term.conditionType}
                            </span>
                          )}
                        </div>
                        {sel && (
                          <button onClick={() => removeSelection(term.termSlug)} className="text-[10px] text-gray-400 hover:text-red-500">
                            Clear
                          </button>
                        )}
                      </div>
                      <select
                        value={sel?.selectedOptionKey || ''}
                        onChange={e => {
                          const v = e.target.value;
                          if (v === '__custom__') {
                            updateSelection(term, '__custom__', '');
                          } else if (v) {
                            updateSelection(term, v);
                          } else {
                            removeSelection(term.termSlug);
                          }
                        }}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-xs bg-white"
                      >
                        <option value="">-- Not selected --</option>
                        {(term.options as any[]).map((opt: any) => (
                          <option key={opt.key} value={opt.key}>{opt.label}{opt.isDefault ? ' (default)' : ''}</option>
                        ))}
                        {term.allowCustom && <option value="__custom__">Custom value...</option>}
                      </select>
                      {isCustomSelected && (
                        <input
                          value={sel?.customValue || ''}
                          onChange={e => updateSelection(term, '__custom__', e.target.value)}
                          placeholder="Type custom value..."
                          className="mt-2 w-full rounded-lg border border-violet-200 bg-violet-50/30 px-3 py-2 text-xs"
                          autoFocus
                        />
                      )}
                      {term.conditionType && term.conditionConfig && (
                        <p className="mt-1.5 text-[10px] text-yellow-600 flex items-center gap-1">
                          <Zap size={9} />
                          Condition: {Object.entries(term.conditionConfig as Record<string, string>).map(([k, v]) => `${k}: ${v}`).join(' | ')}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Preview */}
          {form.selections.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Preview</h4>
              <div className="rounded-lg bg-gray-50 border border-gray-200 p-4 space-y-1.5">
                {form.selections.map((sel: any, si: number) => {
                  const term = terms.find(t => t.termSlug === sel.termSlug);
                  const optLabel = sel.customValue || (term?.options as any[])?.find((o: any) => o.key === sel.selectedOptionKey)?.label || sel.selectedOptionKey;
                  return (
                    <div key={si} className="flex text-xs">
                      <span className="font-medium text-gray-700 w-40 shrink-0">{term?.termName || sel.termSlug}:</span>
                      <span className="text-gray-600">{optLabel}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <button onClick={onClose} className="rounded-lg border px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {isNew ? 'Create Template' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== INSTALLATION REFS TAB ====================

const EMPTY_REF: InstallationReference = {
  id: '', title: '', description: null, productCategoryId: null,
  locationType: 'commercial', location: null, images: [],
  clientName: null, year: null, isActive: true,
};

function InstallRefsTab({ refs, onRefresh }: { refs: InstallationReference[]; onRefresh: () => void }) {
  const [editing, setEditing] = useState<InstallationReference | null>(null);

  const locColors: Record<string, string> = {
    commercial: 'bg-blue-100 text-blue-700',
    domestic: 'bg-green-100 text-green-700',
    industrial: 'bg-orange-100 text-orange-700',
    hospital: 'bg-red-100 text-red-700',
    hotel: 'bg-purple-100 text-purple-700',
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this installation reference?')) return;
    try { await api.delete(`/proposal-builder/installation-refs/${id}`); onRefresh(); } catch {}
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Installation reference photos grouped by product and location type. Upload photos to show in proposals.</p>
        <button
          onClick={() => setEditing({ ...EMPTY_REF })}
          className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-3 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]"
        >
          <Plus size={16} /> Add Reference
        </button>
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {refs.map(ref => (
          <div key={ref.id} className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="text-sm font-semibold text-gray-900">{ref.title}</h4>
                {ref.description && <p className="text-xs text-gray-500 mt-0.5">{ref.description}</p>}
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setEditing(ref)} className="rounded p-1 text-gray-400 hover:text-[var(--ink-0)] hover:bg-[var(--saffron-50)]">
                  <Edit2 size={14} />
                </button>
                <button onClick={() => handleDelete(ref.id)} className="rounded p-1 text-gray-400 hover:text-red-500 hover:bg-red-50">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${locColors[ref.locationType] || 'bg-gray-100 text-gray-600'}`}>{ref.locationType}</span>
              {ref.clientName && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-600">{ref.clientName}</span>}
              {ref.year && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-600">{ref.year}</span>}
              {ref.location && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-600">{ref.location}</span>}
            </div>
            {ref.images.length > 0 ? (
              <div className="mt-3 flex gap-2 overflow-x-auto">
                {ref.images.map((img, i) => (
                  <img key={i} src={img} alt="" className="h-16 w-16 rounded-lg object-cover border" />
                ))}
              </div>
            ) : (
              <button
                onClick={() => setEditing(ref)}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-200 py-4 text-xs text-gray-400 hover:border-[var(--ink-3)] hover:text-[var(--saffron-600)] hover:bg-[var(--saffron-50)]/30 transition-colors cursor-pointer"
              >
                <Plus size={14} /> Click to add photos
              </button>
            )}
          </div>
        ))}
      </div>
      {refs.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <Image size={40} className="mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No installation references configured yet</p>
          <button
            onClick={() => setEditing({ ...EMPTY_REF })}
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]"
          >
            <Plus size={16} /> Add First Reference
          </button>
        </div>
      )}

      {editing && (
        <InstallRefEditModal
          ref_={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onRefresh(); }}
        />
      )}
    </div>
  );
}

function InstallRefEditModal({ ref_, onClose, onSaved }: { ref_: InstallationReference; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<InstallationReference>(ref_);
  const [saving, setSaving] = useState(false);
  const isNew = !ref_.id;

  const handleSave = async () => {
    if (!form.title.trim()) { alert('Title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description || null,
        locationType: form.locationType,
        location: form.location || null,
        clientName: form.clientName || null,
        year: form.year || null,
        images: form.images,
        isActive: form.isActive,
        productCategoryId: form.productCategoryId || null,
      };
      if (isNew) {
        await api.post('/proposal-builder/installation-refs', payload);
      } else {
        await api.put(`/proposal-builder/installation-refs/${form.id}`, payload);
      }
      onSaved();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{isNew ? 'Add Installation Reference' : 'Edit Installation Reference'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          {/* Title + Location Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Title *</label>
              <input
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Heat Pump at Demo Hotel, New Delhi"
                className="w-full rounded-lg border px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Location Type</label>
              <select
                value={form.locationType}
                onChange={e => setForm({ ...form, locationType: e.target.value })}
                className="w-full rounded-lg border px-3 py-2 text-sm"
              >
                <option value="commercial">Commercial</option>
                <option value="domestic">Domestic</option>
                <option value="industrial">Industrial</option>
                <option value="hospital">Hospital</option>
                <option value="hotel">Hotel</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={form.description || ''}
              onChange={e => setForm({ ...form, description: e.target.value || null })}
              rows={2}
              placeholder="Brief description of the installation..."
              className="w-full rounded-lg border px-3 py-2 text-sm"
            />
          </div>

          {/* Client Name + Year + Location */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Client Name</label>
              <input
                value={form.clientName || ''}
                onChange={e => setForm({ ...form, clientName: e.target.value || null })}
                placeholder="e.g. Demo Hotels Pvt Ltd"
                className="w-full rounded-lg border px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Year</label>
              <input
                type="number"
                value={form.year || ''}
                onChange={e => setForm({ ...form, year: e.target.value ? Number(e.target.value) : null })}
                placeholder="2025"
                className="w-full rounded-lg border px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
              <input
                value={form.location || ''}
                onChange={e => setForm({ ...form, location: e.target.value || null })}
                placeholder="e.g. New Delhi"
                className="w-full rounded-lg border px-3 py-2 text-sm"
              />
            </div>
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Installation Photos</label>
            <ImageUploader
              images={form.images}
              onChange={(images) => setForm({ ...form, images })}
              maxImages={10}
              label="Upload installation reference photos"
            />
          </div>

          {/* Active toggle */}
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={e => setForm({ ...form, isActive: e.target.checked })}
                className="rounded"
              />
              Active
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <button onClick={onClose} className="rounded-lg border px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {isNew ? 'Create' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== LITERATURE TAB ====================

const EMPTY_LIT: CompanyLiterature = {
  id: '', name: '', slug: '', type: 'scope_of_supply',
  contentHtml: '', contentJson: null, images: [],
  scope: 'all', productCategoryId: null, isActive: true,
};

function LiteratureTab({ literature, onRefresh }: { literature: CompanyLiterature[]; onRefresh: () => void }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<CompanyLiterature | null>(null);

  const toggle = (id: string) => {
    setExpanded(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this literature entry?')) return;
    try { await api.delete(`/proposal-builder/literature/${id}`); onRefresh(); } catch {}
  };

  const typeColors: Record<string, string> = {
    comparison: 'bg-blue-100 text-blue-700',
    clientele: 'bg-purple-100 text-purple-700',
    brochure: 'bg-green-100 text-green-700',
    faq: 'bg-amber-100 text-amber-700',
    scope_of_supply: 'bg-teal-100 text-teal-700',
    exclusions: 'bg-red-100 text-red-700',
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Company literature — comparison tables, scope of supply, exclusions, clientele, and FAQs.</p>
        <button
          onClick={() => setEditing({ ...EMPTY_LIT })}
          className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-3 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]"
        >
          <Plus size={16} /> Add Literature
        </button>
      </div>
      {literature.map(lit => (
        <div key={lit.id} className="rounded-xl border border-gray-200 bg-white">
          <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => toggle(lit.id)}>
            <BookOpen size={16} className="text-gray-400" />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">{lit.name}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${typeColors[lit.type] || 'bg-gray-100 text-gray-600'}`}>{lit.type.replace('_', ' ')}</span>
                {lit.scope !== 'all' && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-500">{lit.scope}</span>}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={(e) => { e.stopPropagation(); setEditing(lit); }} className="rounded p-1 text-gray-400 hover:text-[var(--ink-0)] hover:bg-[var(--saffron-50)]">
                <Edit2 size={14} />
              </button>
              <button onClick={(e) => { e.stopPropagation(); handleDelete(lit.id); }} className="rounded p-1 text-gray-400 hover:text-red-600 hover:bg-red-50">
                <Trash2 size={14} />
              </button>
              {expanded.has(lit.id) ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
            </div>
          </div>
          {expanded.has(lit.id) && (
            <div className="border-t border-gray-100 px-4 py-3">
              {lit.contentHtml && <div className="prose prose-sm max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: lit.contentHtml }} />}
              {lit.contentJson && (
                <div className="mt-2">
                  {lit.contentJson.columns && lit.contentJson.rows && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs border">
                        <thead><tr>{lit.contentJson.columns.map((c: string, i: number) => <th key={i} className="border bg-gray-50 px-3 py-1.5 text-left font-medium">{c}</th>)}</tr></thead>
                        <tbody>
                          {lit.contentJson.rows.map((row: string[], ri: number) => (
                            <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              {row.map((cell: string, ci: number) => <td key={ci} className="border px-3 py-1.5">{cell}</td>)}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {lit.contentJson.faqs && (
                    <div className="space-y-2">
                      {lit.contentJson.faqs.map((faq: any, i: number) => (
                        <div key={i} className="rounded-lg bg-gray-50 p-3">
                          <p className="text-xs font-semibold text-gray-800">Q: {faq.q}</p>
                          <p className="text-xs text-gray-600 mt-1">A: {faq.a}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {lit.contentJson.clients && (
                    <div className="flex flex-wrap gap-2">
                      {lit.contentJson.clients.map((c: any, i: number) => (
                        <span key={i} className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700">{c.name} <span className="text-gray-400">({c.sector})</span></span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
      {literature.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <BookOpen size={40} className="mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No literature entries configured yet</p>
          <button onClick={() => setEditing({ ...EMPTY_LIT })} className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]">
            <Plus size={16} /> Add First Entry
          </button>
        </div>
      )}

      {editing && (
        <LiteratureEditModal
          lit={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onRefresh(); }}
        />
      )}
    </div>
  );
}

function LiteratureEditModal({ lit, onClose, onSaved }: { lit: CompanyLiterature; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState(() => ({
    ...lit,
    contentJson: lit.contentJson ? JSON.parse(JSON.stringify(lit.contentJson)) : null,
  }));
  const [saving, setSaving] = useState(false);
  const isNew = !lit.id;

  // Initialize contentJson based on type when switching
  const handleTypeChange = (type: string) => {
    let contentJson = form.contentJson;
    if (type === 'faq' && !contentJson?.faqs) contentJson = { faqs: [{ q: '', a: '' }] };
    else if (type === 'clientele' && !contentJson?.clients) contentJson = { clients: [{ name: '', sector: '' }] };
    else if ((type === 'comparison') && !contentJson?.columns) contentJson = { columns: ['Feature', 'Sun Stellar', 'Others'], rows: [['', '', '']] };
    setForm({ ...form, type, contentJson });
  };

  // FAQ helpers
  const addFaq = () => {
    const cj = JSON.parse(JSON.stringify(form.contentJson || { faqs: [] }));
    cj.faqs.push({ q: '', a: '' });
    setForm({ ...form, contentJson: cj });
  };
  const updateFaq = (i: number, field: string, val: string) => {
    const cj = JSON.parse(JSON.stringify(form.contentJson));
    cj.faqs[i][field] = val;
    setForm({ ...form, contentJson: cj });
  };
  const removeFaq = (i: number) => {
    const cj = JSON.parse(JSON.stringify(form.contentJson));
    cj.faqs.splice(i, 1);
    setForm({ ...form, contentJson: cj });
  };

  // Client helpers
  const addClient = () => {
    const cj = JSON.parse(JSON.stringify(form.contentJson || { clients: [] }));
    cj.clients.push({ name: '', sector: '' });
    setForm({ ...form, contentJson: cj });
  };
  const updateClient = (i: number, field: string, val: string) => {
    const cj = JSON.parse(JSON.stringify(form.contentJson));
    cj.clients[i][field] = val;
    setForm({ ...form, contentJson: cj });
  };
  const removeClient = (i: number) => {
    const cj = JSON.parse(JSON.stringify(form.contentJson));
    cj.clients.splice(i, 1);
    setForm({ ...form, contentJson: cj });
  };

  // Comparison table helpers
  const updateCol = (i: number, val: string) => {
    const cj = JSON.parse(JSON.stringify(form.contentJson));
    cj.columns[i] = val;
    setForm({ ...form, contentJson: cj });
  };
  const addCol = () => {
    const cj = JSON.parse(JSON.stringify(form.contentJson));
    cj.columns.push('');
    cj.rows = cj.rows.map((r: string[]) => [...r, '']);
    setForm({ ...form, contentJson: cj });
  };
  const removeCol = (i: number) => {
    const cj = JSON.parse(JSON.stringify(form.contentJson));
    cj.columns.splice(i, 1);
    cj.rows = cj.rows.map((r: string[]) => { r.splice(i, 1); return r; });
    setForm({ ...form, contentJson: cj });
  };
  const updateCell = (ri: number, ci: number, val: string) => {
    const cj = JSON.parse(JSON.stringify(form.contentJson));
    cj.rows[ri][ci] = val;
    setForm({ ...form, contentJson: cj });
  };
  const addRow = () => {
    const cj = JSON.parse(JSON.stringify(form.contentJson));
    cj.rows.push(cj.columns.map(() => ''));
    setForm({ ...form, contentJson: cj });
  };
  const removeRow = (ri: number) => {
    const cj = JSON.parse(JSON.stringify(form.contentJson));
    cj.rows.splice(ri, 1);
    setForm({ ...form, contentJson: cj });
  };

  const handleSave = async () => {
    if (!form.name.trim()) { alert('Name is required'); return; }
    if (!form.slug.trim()) { alert('Slug is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, slug: form.slug, type: form.type, scope: form.scope,
        contentHtml: form.contentHtml || '', contentJson: form.contentJson,
        images: form.images, isActive: form.isActive, productCategoryId: form.productCategoryId,
      };
      if (isNew) {
        await api.post('/proposal-builder/literature', payload);
      } else {
        await api.put(`/proposal-builder/literature/${form.id}`, payload);
      }
      onSaved();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{isNew ? 'Add Literature' : 'Edit Literature'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Scope of Supply — Heat Pump" className="w-full rounded-lg border px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Slug *</label>
              <input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="e.g. scope-of-supply-hp" className="w-full rounded-lg border px-3 py-2 text-sm font-mono" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
              <select value={form.type} onChange={e => handleTypeChange(e.target.value)} className="w-full rounded-lg border px-3 py-2 text-sm">
                <option value="scope_of_supply">Scope of Supply</option>
                <option value="exclusions">Exclusions</option>
                <option value="comparison">Comparison Table</option>
                <option value="clientele">Clientele</option>
                <option value="faq">FAQ</option>
                <option value="brochure">Brochure</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Scope</label>
              <select value={form.scope} onChange={e => setForm({ ...form, scope: e.target.value })} className="w-full rounded-lg border px-3 py-2 text-sm">
                <option value="all">All</option>
                <option value="commercial">Commercial</option>
                <option value="domestic">Domestic</option>
              </select>
            </div>
          </div>

          {/* Content HTML (for scope_of_supply, exclusions, brochure) */}
          {['scope_of_supply', 'exclusions', 'brochure'].includes(form.type) && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Content (HTML)</label>
              <textarea value={form.contentHtml || ''} onChange={e => setForm({ ...form, contentHtml: e.target.value })} rows={8} className="w-full rounded-lg border px-3 py-2 text-sm font-mono" placeholder="<ul><li>Item 1</li></ul>" />
            </div>
          )}

          {/* FAQ Editor */}
          {form.type === 'faq' && form.contentJson?.faqs && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-gray-700">FAQs</label>
                <button onClick={addFaq} className="flex items-center gap-1 text-xs text-[var(--ink-0)] hover:text-[var(--ink-0)]"><Plus size={12} /> Add FAQ</button>
              </div>
              <div className="space-y-3">
                {form.contentJson.faqs.map((faq: any, i: number) => (
                  <div key={i} className="rounded-lg border border-gray-200 p-3 space-y-2">
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-bold text-gray-400 mt-2">Q{i + 1}</span>
                      <input value={faq.q} onChange={e => updateFaq(i, 'q', e.target.value)} placeholder="Question" className="flex-1 rounded border px-2 py-1.5 text-xs" />
                      {form.contentJson.faqs.length > 1 && <button onClick={() => removeFaq(i)} className="text-red-400 hover:text-red-600 mt-1"><X size={14} /></button>}
                    </div>
                    <div className="flex items-start gap-2 ml-6">
                      <textarea value={faq.a} onChange={e => updateFaq(i, 'a', e.target.value)} placeholder="Answer" rows={2} className="flex-1 rounded border px-2 py-1.5 text-xs" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Clientele Editor */}
          {form.type === 'clientele' && form.contentJson?.clients && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-gray-700">Clients</label>
                <button onClick={addClient} className="flex items-center gap-1 text-xs text-[var(--ink-0)] hover:text-[var(--ink-0)]"><Plus size={12} /> Add Client</button>
              </div>
              <div className="space-y-1.5">
                {form.contentJson.clients.map((c: any, i: number) => (
                  <div key={i} className="flex items-center gap-2">
                    <input value={c.name} onChange={e => updateClient(i, 'name', e.target.value)} placeholder="Client name" className="flex-1 rounded border px-2 py-1.5 text-xs" />
                    <input value={c.sector} onChange={e => updateClient(i, 'sector', e.target.value)} placeholder="Sector" className="w-32 rounded border px-2 py-1.5 text-xs" />
                    {form.contentJson.clients.length > 1 && <button onClick={() => removeClient(i)} className="text-red-400 hover:text-red-600"><X size={14} /></button>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Comparison Table Editor */}
          {form.type === 'comparison' && form.contentJson?.columns && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-gray-700">Comparison Table</label>
                <div className="flex items-center gap-2">
                  <button onClick={addCol} className="flex items-center gap-1 text-xs text-[var(--ink-0)] hover:text-[var(--ink-0)]"><Plus size={12} /> Column</button>
                  <button onClick={addRow} className="flex items-center gap-1 text-xs text-[var(--ink-0)] hover:text-[var(--ink-0)]"><Plus size={12} /> Row</button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border">
                  <thead>
                    <tr>
                      {form.contentJson.columns.map((col: string, ci: number) => (
                        <th key={ci} className="border bg-gray-50 px-1 py-1">
                          <div className="flex items-center gap-1">
                            <input value={col} onChange={e => updateCol(ci, e.target.value)} className="w-full rounded border px-2 py-1 text-xs font-medium" />
                            {form.contentJson.columns.length > 2 && <button onClick={() => removeCol(ci)} className="text-red-400 hover:text-red-600"><X size={12} /></button>}
                          </div>
                        </th>
                      ))}
                      <th className="w-8 border bg-gray-50"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(form.contentJson.rows || []).map((row: string[], ri: number) => (
                      <tr key={ri}>
                        {row.map((cell: string, ci: number) => (
                          <td key={ci} className="border px-1 py-0.5">
                            <input value={cell} onChange={e => updateCell(ri, ci, e.target.value)} className="w-full rounded border px-2 py-1 text-xs" />
                          </td>
                        ))}
                        <td className="border px-1 py-0.5">
                          {(form.contentJson.rows || []).length > 1 && <button onClick={() => removeRow(ri)} className="text-red-400 hover:text-red-600"><X size={12} /></button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} className="rounded" />
              Active
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <button onClick={onClose} className="rounded-lg border px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {isNew ? 'Create' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
